import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLazyQuery, useQuery } from '@apollo/client';
import { SEARCH_TRAINING_MEMORIES } from '~/services/memory';
import { Memory } from './MemoriesModule';

interface MemoryFilterProps {
  agentId: string;
  onFilteredResults: (memories: Memory[], isSearchActive: boolean) => void;
  onClearResults: () => void;
}

interface FilterState {
  searchTerm: string;
  tags: string[];
  sourceType: string;
  limit: number;
}

// Move SOURCE_TYPE_OPTIONS inside component to use translations

export const MemoryFilter: React.FC<MemoryFilterProps> = ({
  agentId,
  onFilteredResults,
  onClearResults,
}) => {
  const [t] = useTranslation('training_settings');
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    tags: [],
    sourceType: '',
    limit: 50,
  });

  const SOURCE_TYPE_OPTIONS = [
    { value: '', label: t('all_sources', 'All Sources') },
    { value: 'manual', label: t('manual', 'Manual') },
    { value: 'document', label: t('document', 'Document') },
    { value: 'webpage', label: t('webpage', 'Webpage') },
    { value: 'conversation', label: t('conversation', 'Conversation') },
    { value: 'email', label: t('email', 'Email') },
    { value: 'video', label: t('video', 'Video') },
    { value: 'audio', label: t('audio', 'Audio') },
    { value: 'image', label: t('image', 'Image') },
    { value: 'api_response', label: t('api_response', 'API Response') },
  ];
  const [tagInput, setTagInput] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [searchMemories, { loading, data, error }] = useLazyQuery(
    SEARCH_TRAINING_MEMORIES,
    {
      onCompleted: (searchData) => {
        if (searchData?.search_training_memories?.data) {
          onFilteredResults(searchData.search_training_memories.data, true);
          setIsSearchActive(true);
        }
      },
      onError: (error) => {
        console.error('Search error:', error);
      },
    },
  );

  const handleSearch = () => {
    if (!filters.searchTerm.trim()) {
      return;
    }

    const variables = {
      data: {
        agent_id: agentId,
        searchTerm: filters.searchTerm,
        limit: filters.limit,
        ...(filters.tags.length > 0 && { tags: filters.tags }),
        ...(filters.sourceType && { source_type: filters.sourceType }),
      },
    };

    searchMemories({ variables });
  };

  const handleClearSearch = () => {
    setFilters({
      searchTerm: '',
      tags: [],
      sourceType: '',
      limit: 50,
    });
    setTagInput('');
    setIsSearchActive(false);
    onClearResults();
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !filters.tags.includes(tagInput.trim())) {
      setFilters((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFilters((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: 'search' | 'tag') => {
    if (e.key === 'Enter') {
      if (action === 'search') {
        handleSearch();
      } else if (action === 'tag') {
        handleAddTag();
      }
    }
  };

  return (
    <div className="mb-6 rounded-lg border bg-gray-50 p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t('search_memories', 'Search & Filter Memories')}
        </h3>
        {isSearchActive && (
          <button
            onClick={handleClearSearch}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="mr-1 h-4 w-4" />
            {t('clear_search', 'Clear Search')}
          </button>
        )}
      </div>

      {/* Search Input */}
      <div className="mb-4">
        <label className="mb-2 block text-sm font-medium text-gray-700">
          {t('search_term', 'Search Term')}
        </label>
        <div className="flex">
          <div className="relative flex-1">
            <input
              type="text"
              value={filters.searchTerm}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, searchTerm: e.target.value }))
              }
              onKeyPress={(e) => handleKeyPress(e, 'search')}
              placeholder={t('search_placeholder', 'Search memories...')}
              className="w-full rounded-l-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
          <button
            onClick={handleSearch}
            disabled={!filters.searchTerm.trim() || loading}
            className="rounded-r-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
            ) : (
              t('search', 'Search')
            )}
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* Tags Filter */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t('filter_by_tags', 'Filter by Tags')}
          </label>
          <div className="flex">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'tag')}
              placeholder={t('add_tag', 'Add tag...')}
              className="flex-1 rounded-l-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={handleAddTag}
              disabled={!tagInput.trim()}
              className="rounded-r-md bg-gray-600 px-3 py-2 text-white hover:bg-gray-700 disabled:bg-gray-400"
            >
              +
            </button>
          </div>
          {filters.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {filters.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800"
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Source Type Filter */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t('filter_by_source', 'Filter by Source Type')}
          </label>
          <select
            value={filters.sourceType}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, sourceType: e.target.value }))
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {SOURCE_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Limit Filter */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t('result_limit', 'Result Limit')}
          </label>
          <select
            value={filters.limit}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                limit: parseInt(e.target.value),
              }))
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value={10}>
              {t('results_count', '{{count}} results', { count: 10 })}
            </option>
            <option value={25}>
              {t('results_count', '{{count}} results', { count: 25 })}
            </option>
            <option value={50}>
              {t('results_count', '{{count}} results', { count: 50 })}
            </option>
            <option value={100}>
              {t('results_count', '{{count}} results', { count: 100 })}
            </option>
          </select>
        </div>
      </div>

      {/* Search Results Info */}
      {isSearchActive && data?.search_training_memories?.data && (
        <div className="mt-4 rounded-md bg-blue-50 p-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-blue-800">
              <strong>
                {t('search_results_for', 'Search: "{{term}}"', {
                  term: filters.searchTerm,
                })}
              </strong>
              <span className="ml-2">
                {t('found_results', 'Found {{count}} results', {
                  count: data.search_training_memories.data.length,
                })}
              </span>
            </div>
            <div className="text-xs text-blue-600">
              {t('vector_search_info', 'Vector search with relevance scoring')}
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-3">
          <div className="text-sm text-red-800">
            {t('search_failed', 'Search failed')}: {error.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryFilter;
